import { useEffect, useMemo, useState } from 'react';
import { PageLoader } from '@/components/ui/spinner';
import { useLocation } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { format, startOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import type { DateRange } from 'react-day-picker';
import { FileSpreadsheet, Eye, Trash2, Pencil, Plus, X, ReceiptText, Download, MoreVertical } from 'lucide-react';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { api, ApiCallError } from '@/lib/api';
import { AppShell } from '@/components/app-shell';
import { useTenantMe } from '@/hooks/use-tenant-me';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { DataTable } from '@/components/ui/data-table';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { AppClientPicker } from '@/components/app-client-picker';
import type { ClientRow } from '@/components/app-client-form-dialog';
import {
  DocumentClientBlock,
  DocumentItemsTable,
  DocumentMetaGrid,
  DocumentSection,
  DocumentTotals,
  type TotalLine,
} from '@/components/app-document-detail';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Empty, EmptyHeader, EmptyMedia, EmptyTitle, EmptyDescription } from '@/components/ui/empty';

interface QuoteRow {
  id: string;
  clientId?: string | null;
  quoteNumber: string;
  clientName: string;
  clientRuc: string | null;
  grandTotal: string;
  currency: string;
  status: string;
  validUntil: string | null;
  createdAt: string;
}
interface QuoteItem {
  id: string;
  lineNumber: number;
  description: string;
  quantity: string;
  unitPrice: string;
  subtotal: string;
  taxRate: number;
}
interface QuoteDetail {
  quote: QuoteRow & { subtotal: string; totalTax: string; notes: string | null; clientPhone: string | null };
  items: QuoteItem[];
}

const STATUS_LABEL: Record<string, string> = {
  draft: 'Borrador',
  sent: 'Enviado',
  accepted: 'Aceptado',
  rejected: 'Rechazado',
  expired: 'Vencido',
  invoiced: 'Facturado',
};

function statusVariant(s: string): 'success' | 'warning' | 'destructive' | 'secondary' {
  if (s === 'accepted' || s === 'invoiced') return 'success';
  if (s === 'rejected' || s === 'expired') return 'destructive';
  if (s === 'sent') return 'warning';
  return 'secondary';
}

function formatGs(amount: string | number, currency = 'PYG'): string {
  const n = Number(amount) || 0;
  if (currency === 'PYG') return `Gs ${Math.round(n).toLocaleString('es-PY')}`;
  return `${currency} ${n.toLocaleString('es-PY')}`;
}
const errMsg = (e: unknown, fallback: string) =>
  e instanceof ApiCallError ? (e.body.message ?? e.body.error) : fallback;

export function AppPresupuestosPage() {
  const [, navigate] = useLocation();
  const { data: me, isLoading: loadingMe } = useTenantMe();
  const qc = useQueryClient();
  const [viewId, setViewId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [convertRow, setConvertRow] = useState<QuoteRow | null>(null);
  const [deleteRow, setDeleteRow] = useState<QuoteRow | null>(null);
  const [range, setRange] = useState<DateRange | undefined>({
    from: startOfMonth(new Date()),
    to: new Date(),
  });
  const from = range?.from ? format(range.from, 'yyyy-MM-dd') : '';
  const to = range?.to ? format(range.to, 'yyyy-MM-dd') : '';

  // El rango filtra server-side (reduce carga). Dentro del período, el DataTable
  // hace búsqueda/orden/export client-side sobre el set acotado.
  const { data, isLoading } = useQuery({
    queryKey: ['app-quotes', from, to],
    queryFn: () => {
      const params = new URLSearchParams({ page: '1', pageSize: '1000' });
      if (from && to) {
        params.set('from', from);
        params.set('to', to);
      }
      return api<{ items: QuoteRow[]; total: number }>(`/api/app/quotes?${params}`);
    },
    enabled: !!me,
  });

  const del = useMutation({
    mutationFn: (id: string) => api(`/api/app/quotes/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Presupuesto eliminado.');
      qc.invalidateQueries({ queryKey: ['app-quotes'] });
    },
    onError: (e) => toast.error(errMsg(e, 'No se pudo eliminar')),
  });

  const items = data?.items ?? [];

  const columns = useMemo<ColumnDef<QuoteRow, unknown>[]>(
    () => [
      {
        accessorKey: 'quoteNumber',
        header: 'Presupuesto',
        cell: ({ row }) => <span className="font-mono text-sm">{row.original.quoteNumber || '—'}</span>,
      },
      {
        id: 'cliente',
        header: 'Cliente',
        accessorFn: (q) => `${q.clientName} ${q.clientRuc ?? ''}`,
        cell: ({ row }) => (
          <div>
            <div className="max-w-[14rem] truncate leading-tight">{row.original.clientName || '—'}</div>
            {row.original.clientRuc && (
              <div className="font-mono text-xs text-muted-foreground">{row.original.clientRuc}</div>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Fecha',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm">
            {format(new Date(row.original.createdAt), 'dd MMM yyyy', { locale: es })}
          </span>
        ),
      },
      {
        accessorKey: 'validUntil',
        header: 'Validez',
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-sm text-muted-foreground">
            {row.original.validUntil
              ? format(new Date(row.original.validUntil + 'T12:00:00'), 'dd MMM yyyy', { locale: es })
              : '—'}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Estado',
        cell: ({ row }) => (
          <Badge variant={statusVariant(row.original.status)}>
            {STATUS_LABEL[row.original.status] ?? row.original.status}
          </Badge>
        ),
      },
      {
        id: 'monto',
        header: 'Monto',
        accessorFn: (q) => Number(q.grandTotal),
        cell: ({ row }) => (
          <span className="font-semibold tabular-nums whitespace-nowrap">
            {formatGs(row.original.grandTotal, row.original.currency)}
          </span>
        ),
      },
      {
        id: 'acciones',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const q = row.original;
          return (
            <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" title="Acciones">
                    <MoreVertical className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => setViewId(q.id)}>
                    <Eye /> Ver detalle
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <a href={`/api/app/quotes/${q.id}/pdf`} target="_blank" rel="noopener noreferrer">
                      <Download /> Descargar PDF
                    </a>
                  </DropdownMenuItem>
                  {q.status !== 'invoiced' && (
                    <DropdownMenuItem
                      onClick={() => {
                        setEditId(q.id);
                        setFormOpen(true);
                      }}
                    >
                      <Pencil /> Editar
                    </DropdownMenuItem>
                  )}
                  {q.status !== 'invoiced' && (
                    <DropdownMenuItem onClick={() => setConvertRow(q)}>
                      <ReceiptText /> Facturar
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem variant="destructive" onClick={() => setDeleteRow(q)}>
                    <Trash2 /> Eliminar
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    []
  );

  if (loadingMe) return <PageLoader />;
  if (!me) {
    navigate('/app/login');
    return null;
  }

  return (
    <AppShell>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Presupuestos</h1>
          <p className="text-sm text-muted-foreground">
            Las cotizaciones que generás para tus clientes — por el bot o desde acá.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <DateRangePicker
            value={range}
            onChange={setRange}
            placeholder="Rango de fechas"
            align="end"
            disabled={{ after: new Date() }}
          />
          <Button
            className="w-full sm:w-auto"
            onClick={() => {
              setEditId(null);
              setFormOpen(true);
            }}
          >
            <Plus className="mr-2 size-4" /> Nuevo presupuesto
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      )}

      {!isLoading && items.length === 0 && (
        <Card>
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <FileSpreadsheet />
              </EmptyMedia>
              <EmptyTitle>Sin presupuestos todavía</EmptyTitle>
              <EmptyDescription>
                Pedile al bot un presupuesto (ej: “hacer presupuesto de 2 hs de clases a 100.000 la
                hora”) y aparece acá.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </Card>
      )}

      {!isLoading && items.length > 0 && (
        <DataTable
          columns={columns}
          data={items}
          pageSize={15}
          searchPlaceholder="Buscar por número o cliente…"
          exportFileName="presupuestos"
          exportRows={(rows) =>
            rows.map((q) => ({
              Presupuesto: q.quoteNumber,
              Cliente: q.clientName,
              RUC: q.clientRuc ?? '',
              Fecha: format(new Date(q.createdAt), 'dd/MM/yyyy'),
              Validez: q.validUntil ? format(new Date(q.validUntil + 'T12:00:00'), 'dd/MM/yyyy') : '',
              Estado: STATUS_LABEL[q.status] ?? q.status,
              Monto: Number(q.grandTotal),
              Moneda: q.currency,
            }))
          }
          onRowClick={(q) => setViewId(q.id)}
        />
      )}

      <AlertDialog open={!!deleteRow} onOpenChange={(o) => !o && setDeleteRow(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este presupuesto?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteRow
                ? `${deleteRow.quoteNumber} · ${deleteRow.clientName} · ${formatGs(deleteRow.grandTotal, deleteRow.currency)}. `
                : ''}
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (deleteRow) del.mutate(deleteRow.id);
                setDeleteRow(null);
              }}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <QuoteDetailDialog id={viewId} onClose={() => setViewId(null)} />
      <QuoteFormDialog
        open={formOpen}
        editId={editId}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          qc.invalidateQueries({ queryKey: ['app-quotes'] });
        }}
      />
      <ConvertQuoteDialog
        quote={convertRow}
        onClose={() => setConvertRow(null)}
        onConverted={() => {
          setConvertRow(null);
          qc.invalidateQueries({ queryKey: ['app-quotes'] });
          qc.invalidateQueries({ queryKey: ['app-electronic-invoices'] });
        }}
      />
    </AppShell>
  );
}

function ConvertQuoteDialog({
  quote,
  onClose,
  onConverted,
}: {
  quote: QuoteRow | null;
  onClose: () => void;
  onConverted: () => void;
}) {
  const [saleType, setSaleType] = useState<'contado' | 'credito'>('contado');

  const convert = useMutation({
    mutationFn: () =>
      api<{ invoiceNumber: string }>(`/api/app/quotes/${quote!.id}/convert`, {
        method: 'POST',
        body: JSON.stringify({ saleType, taxRate: 10 }),
      }),
    onSuccess: (r) => {
      toast.success(`Factura ${r.invoiceNumber} emitida.`);
      onConverted();
    },
    onError: (e) => toast.error(errMsg(e, 'No se pudo facturar')),
  });

  return (
    <Dialog open={!!quote} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Convertir en factura electrónica</DialogTitle>
          <DialogDescription>
            {quote ? `${quote.quoteNumber} · ${quote.clientName} · ${formatGs(quote.grandTotal, quote.currency)}` : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="w-44 space-y-1">
            <Label>Condición</Label>
            <Select value={saleType} onValueChange={(v) => setSaleType(v as typeof saleType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="contado">Contado</SelectItem>
                <SelectItem value="credito">Crédito</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <p className="text-xs text-muted-foreground">
            Se emite con IVA 10% incluido — el total no cambia. A crédito hace falta el RUC/CI del
            cliente en la cotización. La cotización queda marcada como facturada.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button disabled={convert.isPending} onClick={() => convert.mutate()}>
            Emitir factura
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ItemRow {
  description: string;
  quantity: string;
  unitPrice: string;
}

function QuoteFormDialog({
  open,
  editId,
  onClose,
  onSaved,
}: {
  open: boolean;
  editId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [clientId, setClientId] = useState<string | null>(null);
  const [clientName, setClientName] = useState('');
  const [clientRuc, setClientRuc] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [notes, setNotes] = useState('');
  const [rows, setRows] = useState<ItemRow[]>([{ description: '', quantity: '1', unitPrice: '' }]);

  const onPickClient = (c: ClientRow | null) => {
    setClientId(c?.id ?? null);
    if (c) {
      setClientName(c.razonSocial || c.name);
      setClientRuc(c.ruc ?? '');
    }
  };

  // En modo edición, traemos el detalle y prellenamos.
  const { data: detail, isLoading: loadingDetail } = useQuery({
    queryKey: ['app-quote', editId],
    queryFn: () => api<QuoteDetail>(`/api/app/quotes/${editId}`),
    enabled: open && !!editId,
  });

  useEffect(() => {
    if (!open) return;
    if (editId && detail) {
      setClientId(detail.quote.clientId ?? null);
      setClientName(detail.quote.clientName ?? '');
      setClientRuc(detail.quote.clientRuc ?? '');
      setValidUntil(detail.quote.validUntil ?? '');
      setNotes(detail.quote.notes ?? '');
      setRows(
        detail.items.length > 0
          ? detail.items.map((it) => ({
              description: it.description,
              quantity: String(Number(it.quantity)),
              unitPrice: String(Math.round(Number(it.unitPrice))),
            }))
          : [{ description: '', quantity: '1', unitPrice: '' }]
      );
    } else if (!editId) {
      // modo "nuevo": limpiar
      setClientId(null);
      setClientName('');
      setClientRuc('');
      setValidUntil('');
      setNotes('');
      setRows([{ description: '', quantity: '1', unitPrice: '' }]);
    }
  }, [open, editId, detail]);

  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: () => {
      const items = rows
        .filter((r) => r.description.trim() && r.unitPrice.trim())
        .map((r) => ({
          description: r.description.trim(),
          quantity: Number(r.quantity) || 1,
          unitPrice: Number(r.unitPrice.replace(/[^\d.-]/g, '')) || 0,
        }));
      const body = {
        clientId,
        clientName: clientName.trim(),
        clientRuc: clientRuc.trim() || null,
        validUntil: validUntil || null,
        notes: notes.trim() || null,
        items,
      };
      return editId
        ? api(`/api/app/quotes/${editId}`, { method: 'PUT', body: JSON.stringify(body) })
        : api('/api/app/quotes', { method: 'POST', body: JSON.stringify(body) });
    },
    onSuccess: () => {
      toast.success(editId ? 'Presupuesto actualizado.' : 'Presupuesto creado.');
      if (editId) qc.invalidateQueries({ queryKey: ['app-quote', editId] });
      onSaved();
    },
    onError: (e) => toast.error(errMsg(e, 'No se pudo guardar')),
  });

  const total = rows.reduce(
    (s, r) => s + (Number(r.quantity) || 0) * (Number(r.unitPrice.replace(/[^\d.-]/g, '')) || 0),
    0
  );
  const valid =
    clientName.trim().length > 0 &&
    rows.some((r) => r.description.trim() && r.unitPrice.trim());

  const setRow = (i: number, k: keyof ItemRow, v: string) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] w-[95vw] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editId ? 'Editar presupuesto' : 'Nuevo presupuesto'}</DialogTitle>
          <DialogDescription>Sin IVA — el precio dictado es el precio final.</DialogDescription>
        </DialogHeader>

        {editId && loadingDetail ? (
          <Skeleton className="h-72 w-full" />
        ) : (
          <FieldGroup>
            <Field>
              <FieldLabel>Cliente</FieldLabel>
              <AppClientPicker
                value={clientId}
                selectedLabel={clientName || undefined}
                onChange={onPickClient}
              />
            </Field>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel htmlFor="q-client">Nombre (o escribilo a mano)</FieldLabel>
                <Input
                  id="q-client"
                  value={clientName}
                  onChange={(e) => {
                    setClientName(e.target.value);
                    setClientId(null);
                  }}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="q-ruc">RUC / CI (opcional)</FieldLabel>
                <Input id="q-ruc" value={clientRuc} onChange={(e) => setClientRuc(e.target.value)} />
              </Field>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field>
                <FieldLabel>Válido hasta (opcional)</FieldLabel>
                <DatePicker value={validUntil} onChange={setValidUntil} />
              </Field>
            </div>

            <Field>
              <div className="flex items-center justify-between">
                <FieldLabel>Ítems</FieldLabel>
                <span className="text-xs text-muted-foreground">Sin IVA</span>
              </div>
              <div className="space-y-2">
                {rows.map((r, i) => {
                  const lineTotal =
                    (Number(r.quantity) || 0) * (Number(r.unitPrice.replace(/[^\d.-]/g, '')) || 0);
                  return (
                    <div key={i} className="space-y-2 rounded-lg border border-border bg-muted/20 p-3">
                      <div className="flex items-start gap-2">
                        <Input
                          value={r.description}
                          onChange={(e) => setRow(i, 'description', e.target.value)}
                          placeholder="Ej: Clase de piano"
                          className="flex-1"
                        />
                        <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0 text-muted-foreground"
                          disabled={rows.length === 1}
                          onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))}
                          title="Quitar ítem"
                        >
                          <X className="size-4" />
                        </Button>
                      </div>
                      <div className="flex flex-wrap items-end gap-2">
                        <div className="w-16 space-y-1">
                          <span className="text-[11px] text-muted-foreground">Cant.</span>
                          <Input
                            inputMode="numeric"
                            value={r.quantity}
                            onChange={(e) => setRow(i, 'quantity', e.target.value)}
                          />
                        </div>
                        <div className="w-32 space-y-1">
                          <span className="text-[11px] text-muted-foreground">P. unit.</span>
                          <Input
                            inputMode="numeric"
                            value={r.unitPrice}
                            onChange={(e) => setRow(i, 'unitPrice', e.target.value)}
                            placeholder="100000"
                          />
                        </div>
                        <div className="ml-auto space-y-1 text-right">
                          <span className="block text-[11px] text-muted-foreground">Subtotal</span>
                          <span className="text-sm font-medium tabular-nums">
                            {lineTotal ? formatGs(lineTotal) : '—'}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <Button
                variant="outline"
                size="sm"
                className="w-fit"
                onClick={() => setRows((rs) => [...rs, { description: '', quantity: '1', unitPrice: '' }])}
              >
                <Plus className="mr-2 size-4" /> Agregar ítem
              </Button>
            </Field>

            <Field>
              <FieldLabel htmlFor="q-notes">Notas (opcional)</FieldLabel>
              <Textarea id="q-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>

            <div className="flex items-center justify-between rounded-lg bg-muted px-4 py-3">
              <span className="text-sm font-medium text-muted-foreground">Total</span>
              <span className="text-xl font-bold tabular-nums">{formatGs(total)}</span>
            </div>
          </FieldGroup>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button disabled={!valid || save.isPending} onClick={() => save.mutate()}>
            {editId ? 'Guardar cambios' : 'Crear presupuesto'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function QuoteDetailDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { data, isLoading } = useQuery({
    queryKey: ['app-quote', id],
    queryFn: () => api<QuoteDetail>(`/api/app/quotes/${id}`),
    enabled: !!id,
  });

  const q = data?.quote;
  const currency = q?.currency ?? 'PYG';
  const hasTax = q ? Number(q.totalTax) > 0 : false;

  const totalLines: TotalLine[] = q
    ? [
        { label: 'Subtotal', value: q.subtotal },
        ...(hasTax ? [{ label: 'IVA', value: q.totalTax }] : []),
        { label: 'Total', value: q.grandTotal, emphasis: true },
      ]
    : [];

  return (
    <Dialog open={!!id} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] w-[95vw] max-w-5xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2">
            <FileSpreadsheet className="size-4 text-muted-foreground" />
            Presupuesto {q?.quoteNumber ?? ''}
          </DialogTitle>
        </DialogHeader>

        {isLoading || !q ? (
          <Skeleton className="h-56 w-full" />
        ) : (
          // min-w-0: el DialogContent es un grid; sin esto el hijo con la tabla no
          // se encoge (min-width:auto) y desborda el modal en vez de scrollear.
          <div className="min-w-0 space-y-5">
            <DocumentClientBlock
              name={q.clientName}
              doc={q.clientRuc}
              badge={<Badge variant={statusVariant(q.status)}>{STATUS_LABEL[q.status] ?? q.status}</Badge>}
            />

            <DocumentMetaGrid
              items={[
                { label: 'Moneda', value: currency },
                {
                  label: 'Fecha',
                  value: format(new Date(q.createdAt), 'dd MMM yyyy', { locale: es }),
                },
                {
                  label: 'Válido hasta',
                  value: q.validUntil
                    ? format(new Date(q.validUntil + 'T12:00:00'), 'dd MMM yyyy', { locale: es })
                    : null,
                },
                { label: 'Teléfono', value: q.clientPhone },
              ]}
            />

            <DocumentSection title="Ítems">
              <DocumentItemsTable items={data.items} currency={currency} showTax={hasTax} />
            </DocumentSection>

            <DocumentTotals lines={totalLines} currency={currency} />

            {q.notes && (
              <DocumentSection title="Notas">
                <p className="rounded-md bg-muted p-3 text-sm text-muted-foreground">{q.notes}</p>
              </DocumentSection>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
